import { SortOrderType } from '@repo/common';
import { Badge, Button } from '@repo/shadcn';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowDown } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Route } from '@/constants';
import { TStockItem } from '@/features/slice/inventory';
import { formatCurrencyInBDT } from '@/utils';

import { useUrlFilter } from '../../../hooks';
import { calculateProfitMargin } from '../../../utils/calculate-profit-margin.util';

const QuantityHeader = () => {
    const { sortBy, sortOrder, setSortBy, setSortOrder } = useUrlFilter();
    const handleToggleSort = () => {
        if (sortBy === 'quantity') {
            if (sortOrder === SortOrderType.ASC) {
                setSortOrder(SortOrderType.DESC);
            } else if (sortOrder === SortOrderType.DESC) {
                setSortOrder(SortOrderType.ASC);
            } else {
                setSortOrder(SortOrderType.ASC);
            }
        }

        if (sortBy !== 'quantity') {
            setSortBy('quantity');
            setSortOrder(SortOrderType.ASC);
        }
    };
    return (
        <div className={'flex justify-end text-right'}>
            <button
                type={'button'}
                onClick={handleToggleSort}
                className={'flex items-center justify-end gap-0.5'}
            >
                <span>Quantity</span>

                <ArrowDown
                    className={`size-4 transition-transform ${
                        sortBy === 'quantity' && sortOrder === SortOrderType.ASC
                            ? 'rotate-180'
                            : 'rotate-0'
                    }`}
                />
            </button>
        </div>
    );
};

const ExpiredQuantityHeader = () => {
    const { sortBy, sortOrder, setSortBy, setSortOrder } = useUrlFilter();
    const handleToggleSort = () => {
        if (sortBy === 'expired_quantity') {
            if (sortOrder === SortOrderType.ASC) {
                setSortOrder(SortOrderType.DESC);
            } else if (sortOrder === SortOrderType.DESC) {
                setSortOrder(SortOrderType.ASC);
            } else {
                setSortOrder(SortOrderType.ASC);
            }
        }

        if (sortBy !== 'expired_quantity') {
            setSortBy('expired_quantity');
            setSortOrder(SortOrderType.ASC);
        }
    };
    return (
        <div className={'flex justify-end text-right'}>
            <button
                type={'button'}
                onClick={handleToggleSort}
                className={'flex items-center justify-end gap-0.5'}
            >
                <span>Expired Quantity</span>{' '}
                <ArrowDown
                    className={`size-4 transition-transform ${
                        sortBy === 'expired_quantity' &&
                        sortOrder === SortOrderType.ASC
                            ? 'rotate-180'
                            : 'rotate-0'
                    }`}
                />
            </button>
        </div>
    );
};

export const stockListColumns: Array<ColumnDef<TStockItem>> = [
    {
        accessorKey: 'name',
        header: () => <div>Product Name</div>,
        cell: ({ row }) => <span className="">{row.original.name}</span>,
    },

    {
        accessorKey: 'brand_name',
        header: () => <div>Brand Name</div>,
        cell: ({ row }) => <span className="">{row.original.brand_name}</span>,
    },

    {
        accessorKey: 'quantity',
        header: QuantityHeader,
        cell: ({ row }) => (
            <div className="text-right">{row.original.current_stock}</div>
        ),
    },

    {
        accessorKey: 'expired_quantity',
        header: ExpiredQuantityHeader,
        cell: ({ row }) => (
            <div className="text-right">{row.original.expired_stock}</div>
        ),
    },

    {
        accessorKey: 'latest_purchase_price',
        header: () => <div className={'text-right'}>Purchase Price</div>,
        cell: ({ row }) => (
            <div className="text-right">
                {formatCurrencyInBDT(row.original.latest_purchase_price ?? 0)}
            </div>
        ),
    },

    {
        accessorKey: 'latest_wholesale_price',
        header: () => <div className={'text-right'}>Wholesale Price</div>,
        cell: ({ row }) => (
            <div className="relative text-right">
                <p className={'pr-4 text-right'}>
                    {formatCurrencyInBDT(
                        row.original.latest_wholesale_price ?? 0,
                    )}
                </p>
                <span className="absolute bottom-0 left-[calc(100%_-_16px)] text-[10px] text-green-600">
                    {calculateProfitMargin(
                        row.original.latest_wholesale_price ?? 0,
                        row.original.latest_retail_price ?? 0,
                    )}
                    %
                </span>
            </div>
        ),
    },
    {
        accessorKey: 'latest_retail_price',
        header: () => <div className={'text-right'}>Retail Price</div>,
        cell: ({ row }) => (
            <div className="relative text-right">
                <p className={'pr-4 text-right'}>
                    {formatCurrencyInBDT(row.original.latest_retail_price ?? 0)}
                </p>

                <span className="absolute bottom-0 left-[calc(100%_-_16px)] text-[10px] text-green-600">
                    {calculateProfitMargin(
                        row.original.latest_purchase_price ?? 0,
                        row.original.latest_retail_price ?? 0,
                    )}
                    %
                </span>
            </div>
        ),
    },

    {
        accessorKey: 'stock_status',
        header: () => <div className={'text-left'}>Status</div>,
        cell: ({ row }) => (
            <StockStatusBadge
                currentStock={row.original.current_stock}
                alertQuantity={row.original.alert_quantity}
            />
        ),
    },

    {
        accessorKey: 'actions',
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => (
            <div className="text-right">
                <Link
                    to={`${Route.STOCK_ALL_PRODUCTS}/products/${row.original.product_variation_id}`}
                >
                    <Button type={'button'} variant={'outline'} size={'sm'}>
                        View Details
                    </Button>
                </Link>
            </div>
        ),
    },
];

const StockStatusBadge = ({
    currentStock,
    alertQuantity,
}: {
    currentStock: number;
    alertQuantity: number;
}) => {
    const isStockEmpty = currentStock === 0;

    if (isStockEmpty) {
        return <Badge variant={'destructive'}>Stock Out</Badge>;
    }

    const isLowStock = currentStock <= alertQuantity;

    if (isLowStock) {
        return (
            <Badge className={'text-nowrap bg-orange-400 text-white'}>
                Low Stock
            </Badge>
        );
    }

    return (
        <Badge className={'text-nowrap bg-green-600 text-white'}>
            In Stock
        </Badge>
    );
};
